import type { RequestOptions } from '@@/plugin-request/request';

export type MutationOptions = {
  /** 显式复用某一次业务操作的键，用于网络超时后的安全重试。 */
  idempotencyKey?: string;
  /** 业务动作标识，便于后端日志和监控定位。 */
  actionId?: string;
  /** 需要明确开启一次新操作时跳过短时指纹复用。 */
  newOperation?: boolean;
  /** 查询类 POST 或其他特殊请求可以关闭自动幂等头。 */
  disabled?: boolean;
};

type RequestWithMutation = RequestOptions & {
  mutation?: MutationOptions;
};

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const KEY_TTL_MS = 30_000;
const generatedKeys = new Map<string, { key: string; expiresAt: number }>();

const randomKey = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `mutation-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const stableStringify = (value: unknown): string => {
  if (value === undefined) return 'undefined';
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    return JSON.stringify(
      Array.from(value.entries()).map(([key, item]) => [
        key,
        typeof File !== 'undefined' && item instanceof File
          ? {
              name: item.name,
              size: item.size,
              type: item.type,
              lastModified: item.lastModified,
            }
          : String(item),
      ]),
    );
  }
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  return `{${Object.keys(value as Record<string, unknown>)
    .sort()
    .map(
      (key) =>
        `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`,
    )
    .join(',')}}`;
};

export const isMutationRequest = (options?: RequestOptions) =>
  MUTATION_METHODS.has(String(options?.method || 'GET').toUpperCase());

export const getMutationOptions = (
  options?: RequestOptions,
): MutationOptions | undefined =>
  (options as RequestWithMutation | undefined)?.mutation;

export const getMutationRequestFingerprint = (
  url: string,
  options?: RequestOptions,
  scope = '',
) =>
  stableStringify([
    scope,
    String(options?.method || 'GET').toUpperCase(),
    url,
    options?.data,
    options?.params,
  ]);

/**
 * 为同一浏览器标签页内的快速重复提交生成短时稳定键。
 * 这只负责让重复点击共享同一个后端幂等操作；跨刷新或响应丢失时应传入显式 key。
 */
export const getIdempotencyKey = (
  url: string,
  options?: RequestOptions,
  scope = '',
): string | undefined => {
  if (!isMutationRequest(options)) return undefined;
  const mutation = getMutationOptions(options);
  if (mutation?.disabled) return undefined;
  if (mutation?.idempotencyKey) return mutation.idempotencyKey;
  if (mutation?.newOperation) return randomKey();

  const fingerprint = getMutationRequestFingerprint(url, options, scope);
  const now = Date.now();
  const current = generatedKeys.get(fingerprint);
  if (current && current.expiresAt > now) return current.key;
  const key = randomKey();
  generatedKeys.set(fingerprint, { key, expiresAt: now + KEY_TTL_MS });
  for (const [storedFingerprint, value] of generatedKeys) {
    if (value.expiresAt <= now) generatedKeys.delete(storedFingerprint);
  }
  return key;
};

export const getMutationActionId = (url: string, options?: RequestOptions) =>
  getMutationOptions(options)?.actionId ||
  `${String(options?.method || 'GET').toUpperCase()}:${url}`;
